import React, { memo, useMemo } from 'react';
import { useFeatureFlags, useLoggedInUser } from '@tcc/shared-data-lib';
import { useUserLoggedInConfigs, useFeatureFlagsConfigs } from '../../utils/sharedData';
import { importFromS3 } from '../../utils/importFromS3';
import { Operation } from '../../hooks/useOperations/types';
import { MarketingBannerContainer } from '../../components/MarketingBanner';

interface IProps {
  operationsSelected: Operation[];
}

const MF_FLAG = 'web.fieldSelection.module-federation';
const MF_OVERRIDE_KEY = 'field-selection-web';

const MFFieldSelectionPlugin: React.FC<IProps> = React.lazy(() =>
  import('fieldSelectionWeb/FieldSelectionPlugin')
);
const S3FieldSelectionPlugin: React.FC<IProps> = React.lazy(() =>
  importFromS3('field-selection-web')
);

const MARKETING_BANNER_FF = 'web.field-selection.marketing-notification-banner';

export const FieldSelectionPluginContainer = memo(
  ({ operationsSelected }: IProps) => {
    const { user } = useLoggedInUser(useUserLoggedInConfigs);
    const { featureFlags, isLoading } = useFeatureFlags(useFeatureFlagsConfigs(user));

    const useModuleFederation =
      Boolean(featureFlags?.[MF_FLAG]) ||
      (typeof window !== 'undefined' && Boolean(window.sessionStorage.getItem(MF_OVERRIDE_KEY)));

    const FieldSelectionPlugin = useMemo(() => {
      if (useModuleFederation) {
        return MFFieldSelectionPlugin;
      }
      return S3FieldSelectionPlugin;
    }, [useModuleFederation]);

    const isMarketingBannerEnabled = !isLoading && featureFlags?.[MARKETING_BANNER_FF];

    return (
      <>
        {isMarketingBannerEnabled && <MarketingBannerContainer />}
        <FieldSelectionPlugin operationsSelected={operationsSelected} />
      </>
    );
  }
);
